import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, BarChart3, ChevronsUpDown, Download, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  descargarOfertasCapacidadXlsx,
  getOfertasCapacidad,
  getOfertasCapacidadRango,
  type OfertasCapacidadFila,
} from "@/lib/api-mercado";

const SISTEMAS = ["SIN", "BCA", "BCS"] as const;

const num = (n: number | null, d = 3) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);

// Columnas de la tabla: clave del dato + encabezado + alineación.
const COLS: { key: keyof OfertasCapacidadFila; label: string; right?: boolean }[] = [
  { key: "sistema", label: "Sistema" },
  { key: "fecha", label: "Fecha" },
  { key: "hora", label: "Hora", right: true },
  { key: "term_bloques_mas_pmin_mw", label: "Térmica bloques + Pmin (MW)", right: true },
  { key: "hidro_pmax_mw", label: "Hidro Pmax (MW)", right: true },
  { key: "interm_mw_bloque_01", label: "Intermitentes bloque 01 (MW)", right: true },
  { key: "nodesp_pot_media_mw", label: "No despachables pot. media (MW)", right: true },
  { key: "total_mw", label: "Total (MW)", right: true },
];

// Reporte de capacidad ofertada al MTR por hora y sistema, exportable a Excel.
// Fuente: public.v_ofertas_capacidad_mtr_agg_sist (ETL del MTR).
export function OfertasCapacidadPage() {
  // Arrancan VACÍOS a propósito: el mes se elige cuando se sabe hasta dónde
  // hay datos (el ETL del MTR trae rezago, así que el mes en curso suele estar
  // vacío y no tiene caso ofrecerlo ni consultarlo).
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  // Multiselección de sistema; al menos uno debe quedar activo.
  const [sistemas, setSistemas] = useState<string[]>(["SIN"]);
  const [bajando, setBajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Orden de la tabla (clic en encabezados); default: como viene de la BD.
  const [sortKey, setSortKey] = useState<keyof OfertasCapacidadFila | "">("");
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  // Rango con datos: acota los selectores para no ofrecer meses vacíos.
  const rangoQ = useQuery({
    queryKey: ["ofertas-capacidad-rango"],
    queryFn: getOfertasCapacidadRango,
    staleTime: 60 * 60_000,
  });
  const mesMin = rangoQ.data?.mes_ini ?? undefined;
  const mesMax = rangoQ.data?.mes_fin ?? undefined;

  // Al conocerse el rango: default = el ÚLTIMO mes con datos, y si el usuario
  // ya tenía meses elegidos se encuadran dentro del rango.
  useEffect(() => {
    if (!mesMin || !mesMax) return;
    const clamp = (m: string) => (m < mesMin ? mesMin : m > mesMax ? mesMax : m);
    setDesde((d) => (d ? clamp(d) : mesMax));
    setHasta((h) => (h ? clamp(h) : mesMax));
  }, [mesMin, mesMax]);

  // La consulta NO se dispara al cambiar filtros: solo al presionar "Buscar"
  // (los cortes grandes son pesados). `aplicado` congela los filtros buscados.
  const [aplicado, setAplicado] = useState<{
    desde: string;
    hasta: string;
    sistemas: string[];
  } | null>(null);

  const q = useQuery({
    queryKey: [
      "ofertas-capacidad",
      aplicado?.desde,
      aplicado?.hasta,
      aplicado?.sistemas.join(","),
    ],
    queryFn: () => getOfertasCapacidad(aplicado!.desde, aplicado!.hasta, aplicado!.sistemas),
    enabled: !!aplicado,
    retry: false,
  });

  const filtrosListos = sistemas.length > 0 && !!desde && !!hasta && desde <= hasta;
  const filtrosCambiaron =
    !!aplicado &&
    (aplicado.desde !== desde ||
      aplicado.hasta !== hasta ||
      aplicado.sistemas.join(",") !== sistemas.join(","));

  const buscar = () => {
    if (!filtrosListos) return;
    setError(null);
    setAplicado({ desde, hasta, sistemas: [...sistemas] });
  };

  const toggleSistema = (s: string) =>
    setSistemas((prev) =>
      prev.includes(s) ? (prev.length > 1 ? prev.filter((x) => x !== s) : prev) : [...prev, s],
    );

  const descargar = async () => {
    if (!aplicado) return;
    setBajando(true);
    setError(null);
    try {
      // Descarga EXACTAMENTE el corte buscado (no los filtros a medio editar).
      await descargarOfertasCapacidadXlsx(aplicado.desde, aplicado.hasta, aplicado.sistemas);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo generar el Excel");
    } finally {
      setBajando(false);
    }
  };

  const disp = q.data?.disponible;
  const filas = useMemo(() => {
    const base = q.data?.filas ?? [];
    if (!sortKey) return base;
    return [...base].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulos al final
      if (vb == null) return -1;
      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), "es");
      return cmp * sortDir;
    });
  }, [q.data, sortKey, sortDir]);

  const toggleSort = (k: keyof OfertasCapacidadFila) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-slate-500" /> Ofertas de capacidad (MTR)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Capacidad ofertada al Mercado de Tiempo Real por hora y sistema: térmica (bloques +
          Pmin), hidro (Pmax), intermitentes (bloque 01) y no despachables (potencia media).
        </p>
      </div>

      {/* Filtros: rango de meses + sistemas (multiselección) */}
      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Desde (Año-Mes)</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={desde}
            min={mesMin}
            max={hasta || mesMax}
            title={mesMin ? `Hay datos de ${mesMin} a ${mesMax}` : undefined}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              setDesde(mesMin && v < mesMin ? mesMin : mesMax && v > mesMax ? mesMax : v);
            }}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Hasta (Año-Mes)</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={hasta}
            min={desde || mesMin}
            max={mesMax}
            title={mesMin ? `Hay datos de ${mesMin} a ${mesMax}` : undefined}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              setHasta(mesMin && v < mesMin ? mesMin : mesMax && v > mesMax ? mesMax : v);
            }}
          />
        </label>
        <div>
          <span className="block text-xs font-medium text-slate-600 mb-1">Sistema</span>
          <div className="flex gap-1.5">
            {SISTEMAS.map((s) => {
              const on = sistemas.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSistema(s)}
                  title={
                    on && sistemas.length === 1
                      ? "Debe quedar al menos un sistema seleccionado"
                      : on
                        ? `Quitar ${s}`
                        : `Agregar ${s}`
                  }
                  className={`text-sm border rounded px-3 py-1.5 ${
                    on
                      ? "bg-blue-600 text-white border-blue-600"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={buscar}
          disabled={!filtrosListos || q.isFetching}
          title={filtrosListos ? undefined : "Elige el periodo y al menos un sistema"}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="w-4 h-4" /> {q.isFetching ? "Buscando…" : "Buscar"}
        </button>
        <button
          type="button"
          onClick={descargar}
          disabled={bajando || q.isLoading || filas.length === 0}
          title={aplicado ? "Descarga el corte buscado" : "Primero presiona Buscar"}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" /> {bajando ? "Generando…" : "Descargar Excel"}
        </button>
        <span className="text-[11px] text-slate-400 pb-2 ml-auto">
          {q.isFetching
            ? "Cargando…"
            : aplicado
              ? `${filas.length.toLocaleString("es-MX")} filas`
              : "sin buscar"}
          {disp?.ini && ` · datos disponibles ${disp.ini} a ${disp.fin}`}
          {!disp && mesMin && ` · datos disponibles desde ${mesMin} hasta ${mesMax}`}
        </span>
      </div>

      {filtrosCambiaron && !q.isFetching && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded p-3 text-sm">
          Cambiaste los filtros — la tabla sigue mostrando {aplicado!.desde} a {aplicado!.hasta}{" "}
          ({aplicado!.sistemas.join(", ")}). Presiona <b>Buscar</b> para actualizar.
        </div>
      )}

      {desde > hasta && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          El mes final debe ser igual o posterior al inicial.
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message ?? "No se pudo consultar el reporte."}
        </div>
      )}
      {q.data?.truncado && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          El corte superó el límite de {q.data.limite.toLocaleString("es-MX")} filas y se
          recortó. Acota el rango de meses o los sistemas para verlo completo.
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
              <tr className="text-xs text-slate-500">
                {COLS.map((c) => {
                  const activa = sortKey === c.key;
                  const Icono = activa ? (sortDir === 1 ? ArrowUp : ArrowDown) : ChevronsUpDown;
                  return (
                    <th
                      key={c.key}
                      className={`px-3 py-2 ${c.right ? "text-right" : "text-left"}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        title="Ordenar por esta columna"
                        className={`inline-flex items-center gap-1 hover:text-slate-700 ${
                          c.right ? "flex-row-reverse" : ""
                        } ${activa ? "text-slate-700 font-semibold" : ""}`}
                      >
                        <span>{c.label}</span>
                        <Icono
                          className={`w-3 h-3 ${activa ? "text-slate-500" : "text-slate-300"}`}
                        />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!aplicado && !q.isFetching ? (
                <tr>
                  <td colSpan={COLS.length} className="px-4 py-6 text-sm text-slate-500">
                    Elige el periodo y los sistemas, y presiona <b>Buscar</b> para cargar la
                    información.
                  </td>
                </tr>
              ) : q.isFetching || rangoQ.isLoading ? (
                <tr>
                  <td colSpan={COLS.length} className="px-4 py-6 text-sm text-slate-500">
                    Cargando…
                  </td>
                </tr>
              ) : filas.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length} className="px-4 py-6 text-sm text-slate-500">
                    Sin datos para el periodo y los sistemas buscados.
                  </td>
                </tr>
              ) : (
                filas.map((f: OfertasCapacidadFila, i) => (
                  <tr key={`${f.sistema}-${f.fecha}-${f.hora}-${i}`} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-1.5 font-medium">{f.sistema}</td>
                    <td className="px-3 py-1.5 tabular-nums">{f.fecha}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{f.hora}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {num(f.term_bloques_mas_pmin_mw)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(f.hidro_pmax_mw)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {num(f.interm_mw_bloque_01)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {num(f.nodesp_pot_media_mw)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                      {num(f.total_mw)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
